import { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Icon } from '../components/Icon';

export function Auth() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const send = async () => {
    setBusy(true); setErr(null);
    // Don't pass emailRedirectTo - we want OTP code entry, not a redirect
    // that opens in Safari and breaks the PWA session.
    const { error } = await supabase.auth.signInWithOtp({ email });
    setBusy(false);
    if (error) setErr(error.message);
    else setSent(true);
  };

  const handleOtpChange = (idx: number, val: string) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    // Auto-focus next input
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
    // Auto-submit when all 6 digits entered
    if (val && idx === 5 && next.every((d) => d)) verifyOtp(next.join(''));
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setOtp(next);
    if (pasted.length === 6) verifyOtp(pasted);
    else inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const verifyOtp = async (code: string) => {
    setVerifying(true); setErr(null);
    const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
    setVerifying(false);
    if (error) {
      setErr(error.message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    // On success, onAuthStateChange in store.ts picks up the session automatically
  };

  return (
    <div className="h-full flex flex-col items-center justify-center px-8 safe-top safe-bottom">
      <div className="w-12 h-12 rounded-2xl bg-forest-500 text-cream-50 flex items-center justify-center mb-5">
        <Icon name="book" className="w-6 h-6" />
      </div>
      <h1 className="font-display text-[36px] leading-none">Doomscroll</h1>
      <p className="text-[13px] text-ink-500 mt-2 text-center max-w-[280px]">Read your library, not the feed. Sign in to sync highlights to SiYuan.</p>

      {sent ? (
        <div className="mt-8 w-full max-w-[320px]">
          <p className="text-[14px] text-center mb-1">Enter the 6-digit code sent to</p>
          <p className="text-[14px] font-semibold text-center mb-5">{email}</p>
          <div className="flex justify-center gap-2 mb-4" onPaste={handleOtpPaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                className="w-12 h-14 text-center text-[22px] font-semibold rounded-2xl bg-paper shadow-soft focus:outline-none focus:ring-2 focus:ring-forest-500 tabular-nums"
                disabled={verifying}
              />
            ))}
          </div>
          {verifying && <p className="text-[13px] text-ink-500 text-center mb-3">Verifying...</p>}
          {err && <p className="text-[12px] text-coral text-center mb-3">{err}</p>}
          <div className="flex items-center justify-center gap-4 mt-2">
            <button onClick={() => { setSent(false); setOtp(['', '', '', '', '', '']); setErr(null); }} className="text-[12px] text-forest-500 underline">
              Different email
            </button>
            <button onClick={send} disabled={busy} className="text-[12px] text-forest-500 underline">
              {busy ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-8 w-full max-w-[320px] space-y-3">
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 px-4 rounded-2xl bg-paper shadow-soft text-[14px] focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
          <button onClick={send} disabled={!email || busy} className="w-full h-12 rounded-full bg-ink-900 text-cream-50 font-medium text-[14px] disabled:opacity-40">
            {busy ? 'Sending...' : 'Send login code'}
          </button>
          {err && <p className="text-[12px] text-coral">{err}</p>}
        </div>
      )}
    </div>
  );
}
